import { motion } from "framer-motion";
import { BookOpen, Landmark, LineChart, Compass, HandCoins, ArrowUpRight } from "lucide-react";

const services = [
  {
    id: "bookkeeping",
    num: "01",
    icon: BookOpen,
    title: "Bookkeeping & Accounting",
    span: "md:col-span-7",
    desc: "Clean, audit-ready books every month. Month-end close, AP/AR, payroll oversight, and bank reconciliations handled with controller-level discipline.",
    tags: ["Month-End Close", "AP / AR", "Reconciliations", "QuickBooks · Sage Intacct · Financial Edge"],
  },
  {
    id: "tax",
    num: "02",
    icon: Landmark,
    title: "Tax Preparation & Planning",
    span: "md:col-span-5",
    desc: "Individual and business returns with year-round strategy — so April is a formality, not a surprise. Sales & use tax compliance for contractors included.",
    tags: ["Individual & Business Returns", "Tax Strategy", "IRS Resolution"],
  },
  {
    id: "cfo",
    num: "03",
    icon: LineChart,
    title: "Fractional CFO & Controller",
    span: "md:col-span-5",
    desc: "Board-level finance leadership without the full-time cost. Budgets from $5M–$60M, cash forecasting, grant compliance under OMB 2 CFR 200, and single audit readiness.",
    tags: ["Budget & Forecasting", "Grant Compliance", "Board Reporting", "Internal Controls"],
  },
  {
    id: "coaching",
    num: "04",
    icon: Compass,
    title: "Career Coaching",
    span: "md:col-span-7",
    desc: "A roadmap for immigrant professionals entering the US finance market — which credentials to pursue, how to position your story, and how to land the role.",
    tags: ["CPA / CMA / EA Navigation", "Resume & LinkedIn", "Interview Prep", "US Market Entry"],
  },
  {
    id: "strategies",
    num: "05",
    icon: HandCoins,
    title: "Financial Solution Strategies",
    span: "md:col-span-12",
    desc: "Practical money game plans for individual families and small business owners — budgeting that sticks, debt payoff and savings roadmaps, retirement planning, saving for your children's education, and cash-flow strategies that turn financial stress into a clear, confident plan.",
    tags: ["Family Budgeting", "Debt Payoff & Savings Roadmaps", "Retirement Planning", "Children's Education Savings", "Cash-Flow Strategy", "Small Business Finances"],
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
      >
        What We Do
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
        className="mb-20 max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight text-pine md:text-5xl"
        data-testid="services-heading"
      >
        Five disciplines. <span className="italic text-forest">One standard:</span> the books tell the truth.
      </motion.h2>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
        {services.map((s, i) => (
          <motion.article
            key={s.id}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: (i % 2) * 0.1, ease: [0.76, 0, 0.24, 1] }}
            whileHover={{ y: -4 }}
            data-testid={`service-card-${s.id}`}
            className={`group border border-linen bg-white p-10 transition-shadow duration-500 hover:shadow-xl hover:shadow-black/5 md:p-12 ${s.span}`}
          >
            <div className="mb-10 flex items-start justify-between">
              <span className="font-mono text-sm text-terra">{s.num}</span>
              <s.icon size={28} strokeWidth={1.5} className="text-forest" />
            </div>
            <h3 className="mb-4 font-serif text-2xl font-semibold tracking-tight text-pine md:text-3xl">
              {s.title}
            </h3>
            <p className="mb-8 max-w-lg text-sm leading-relaxed text-pine/70">{s.desc}</p>
            <div className="flex flex-wrap gap-2">
              {s.tags.map((t) => (
                <span key={t} className="border border-linen px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-pine/60">
                  {t}
                </span>
              ))}
            </div>
            <a
              href="#contact"
              data-testid={`service-cta-${s.id}`}
              className="mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:text-terra"
            >
              Discuss this service <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
